import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { LANGUAGE_FLAGS, LANGUAGE_LABELS, CONDITION_LABELS, SOURCE_LABELS, formatPrice, formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { ArrowLeft, Plus, TrendingUp, TrendingDown, Minus, ExternalLink, Heart, BookMarked, Bookmark, Bell, Crown } from "lucide-react";
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import Seo from "@/components/Seo";
import CardNavigator from "@/components/CardNavigator";
import QuantityStepper from "@/components/QuantityStepper";
import { cardCache, priceCache } from "@/lib/cardCache";
import { getCardListContext } from "@/lib/cardContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const MARKETS = [
  { id: "eu", labelKey: "card_detail.market_eu", flag: "🇪🇺", currency: "EUR" },
  { id: "us", labelKey: "card_detail.market_us", flag: "🇺🇸", currency: "USD" },
  { id: "jp", labelKey: "card_detail.market_jp", flag: "🇯🇵", currency: "JPY", pending: true },
];

export default function CardDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const { format: fmtCurrency } = useCurrency();
  const nav = useNavigate();
  const [card, setCard] = useState(() => cardCache.get(`${id}:${i18n.language}`) || null);
  const [priceData, setPriceData] = useState(() => priceCache.get(id) || { entries: [], aggregates: [] });
  // Don't show loading flash if we already have cached data — feels instant.
  const [loading, setLoading] = useState(() => !cardCache.get(`${id}:${i18n.language}`));
  const [showForm, setShowForm] = useState(false);
  // Set of statuses ("owned" | "wishlist" | "favorite") the user has on THIS card
  const [ownedStatuses, setOwnedStatuses] = useState(new Set());
  // Quantity owned for this card (0 means not owned)
  const [ownedQuantity, setOwnedQuantity] = useState(0);

  const loadPrices = () =>
    api.get(`/prices/${id}`).then((r) => {
      priceCache.set(id, r.data);
      setPriceData(r.data);
    });

  // Main fetch — uses cache for instant render, refreshes in background.
  useEffect(() => {
    const cacheKey = `${id}:${i18n.language}`;
    const cached = cardCache.get(cacheKey);
    const cachedPrices = priceCache.get(id);
    if (cached) {
      setCard(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    if (cachedPrices) setPriceData(cachedPrices);

    const fetchCard = api.get(`/cards/${id}`).then((r) => {
      cardCache.set(cacheKey, r.data);
      setCard(r.data);
    });
    Promise.all([fetchCard, loadPrices()])
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, i18n.language]);

  // Prefetch neighbour cards from the list context for instant swipe.
  useEffect(() => {
    const ctx = getCardListContext();
    if (!ctx || !Array.isArray(ctx.ids)) return;
    const idx = ctx.ids.indexOf(id);
    if (idx === -1) return;
    const neighbours = [ctx.ids[idx - 1], ctx.ids[idx + 1]].filter(Boolean);
    neighbours.forEach((nid) => {
      const ck = `${nid}:${i18n.language}`;
      if (!cardCache.get(ck)) {
        api.get(`/cards/${nid}`).then((r) => cardCache.set(ck, r.data)).catch(() => {});
      }
      if (!priceCache.get(nid)) {
        api.get(`/prices/${nid}`).then((r) => priceCache.set(nid, r.data)).catch(() => {});
      }
      // Browser image preload — makes the next-card image render instantly.
      const cached = cardCache.get(ck);
      const imgUrl = cached?.images?.large;
      if (imgUrl) {
        const im = new Image();
        im.src = imgUrl;
      }
    });
  }, [id, i18n.language]);

  // Load this card's ownership / wishlist / favorite state once user + id are known.
  useEffect(() => {
    if (!user?.email) {
      setOwnedStatuses(new Set());
      setOwnedQuantity(0);
      return;
    }
    Promise.all([
      api.get(`/collection/owned-ids`, { params: { status: "owned" } }).catch(() => ({ data: { card_ids: [] } })),
      api.get(`/collection/owned-ids`, { params: { status: "wishlist" } }).catch(() => ({ data: { card_ids: [] } })),
      api.get(`/collection/owned-ids`, { params: { status: "favorite" } }).catch(() => ({ data: { card_ids: [] } })),
      api.get(`/collection`, { params: { status: "owned" } }).catch(() => ({ data: [] })),
    ]).then(([owned, wish, fav, allOwned]) => {
      const set = new Set();
      if (owned.data.card_ids.includes(id)) set.add("owned");
      if (wish.data.card_ids.includes(id)) set.add("wishlist");
      if (fav.data.card_ids.includes(id)) set.add("favorite");
      setOwnedStatuses(set);
      const ownItem = (allOwned.data || []).find((it) => it.card_id === id);
      setOwnedQuantity(ownItem ? (ownItem.quantity || 1) : 0);
    });
  }, [user?.email, id]);

  const filteredEntries = priceData.entries;

  const chartData = useMemo(() => {
    return [...filteredEntries]
      .sort((a, b) => (a.sale_date > b.sale_date ? 1 : -1))
      .map((e) => ({ date: e.sale_date, price: e.price, currency: e.currency }));
  }, [filteredEntries]);

  const toggleCollection = async (status) => {
    if (!user) return nav("/login");
    const wasOwned = ownedStatuses.has(status);
    // Optimistic UI
    const optimistic = new Set(ownedStatuses);
    if (wasOwned) optimistic.delete(status);
    else optimistic.add(status);
    setOwnedStatuses(optimistic);
    try {
      const { data } = await api.post("/collection/toggle", {
        card_id: id,
        status,
        quantity: 1,
        language: "en",
        condition: "near_mint",
      });
      // The backend tells us the resulting state — keep our local set in sync.
      setOwnedStatuses((cur) => {
        const next = new Set(cur);
        if (data?.owned) next.add(status);
        else next.delete(status);
        return next;
      });
      const labelKey = data?.owned ? "card_detail.added_to" : "card_detail.removed_from";
      toast.success(t(labelKey, { status: t(`card_detail.${status}`) }));
    } catch (e) {
      // Roll back optimistic update on failure
      setOwnedStatuses((cur) => {
        const next = new Set(cur);
        if (wasOwned) next.add(status);
        else next.delete(status);
        return next;
      });
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return <div className="text-center text-white/40 font-mono py-32 tracking-widest text-xs">{t("card_detail.loading")}</div>;
  }
  if (!card) return <div className="text-center py-32 text-white/40">{t("card_detail.not_found")}</div>;

  return (
    <div className="page-enter max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
      <Seo
        title={`${card.name} · ${card.set?.name || "Pokémon TCG"} · #${card.number}`}
        description={`Live market prices for ${card.name} from ${card.set?.name || "Pokémon TCG"} (#${card.number}). Track multi-language sales: English, Japanese, French, German, Spanish, Italian.`}
        image={card.images?.large || card.images?.small}
        url={`${window.location.origin}/cards/${card.id}`}
        type="product"
        keywords={`${card.name}, Pokémon card, ${card.set?.name || ""}, ${card.rarity || ""}, TCG price, prix carte pokemon`}
      />
      <Link to={card.set?.id ? `/sets/${card.set.id}` : "/sets"} data-testid="card-back-btn" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#E11D48] mb-6">
        <ArrowLeft className="w-4 h-4" /> {t("card_detail.back_to")} {card.set?.name || "sets"}
      </Link>

      <div className="grid lg:grid-cols-[420px,1fr] gap-6 lg:gap-10">
        {/* CARD IMAGE */}
        <div>
          <CardNavigator currentCardId={id}>
            <div className="relative max-w-sm mx-auto lg:max-w-none">
              {card.images?.large && (
                <img src={card.images.large} alt={card.name} className="w-full max-h-[80vh] md:max-h-none object-contain rounded-2xl shadow-lg" />
              )}
            </div>
          </CardNavigator>
          {user && user.email && (
            <>
              <div className="grid grid-cols-3 gap-2 mt-5">
                <button
                  onClick={() => toggleCollection("owned")}
                  data-testid="add-owned-btn"
                  aria-pressed={ownedStatuses.has("owned")}
                  className={`text-xs flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border transition ${
                    ownedStatuses.has("owned")
                      ? "bg-emerald-500 text-white border-emerald-500 shadow-sm"
                      : "bg-white text-slate-700 border-slate-200 hover:border-emerald-500 hover:text-emerald-600"
                  }`}
                >
                  <BookMarked className={`w-3.5 h-3.5 ${ownedStatuses.has("owned") ? "text-white" : "text-emerald-600"}`} /> {t("card_detail.owned")}
                </button>
                <button
                  onClick={() => toggleCollection("wishlist")}
                  data-testid="add-wishlist-btn"
                  aria-pressed={ownedStatuses.has("wishlist")}
                  className={`text-xs flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border transition ${
                    ownedStatuses.has("wishlist")
                      ? "bg-blue-500 text-white border-blue-500 shadow-sm"
                      : "bg-white text-slate-700 border-slate-200 hover:border-blue-500 hover:text-blue-600"
                  }`}
                >
                  <Bookmark className={`w-3.5 h-3.5 ${ownedStatuses.has("wishlist") ? "text-white" : "text-[#2563EB]"}`} /> {t("card_detail.wishlist")}
                </button>
                <button
                  onClick={() => toggleCollection("favorite")}
                  data-testid="add-favorite-btn"
                  aria-pressed={ownedStatuses.has("favorite")}
                  className={`text-xs flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border transition ${
                    ownedStatuses.has("favorite")
                      ? "bg-[#E11D48] text-white border-[#E11D48] shadow-sm"
                      : "bg-white text-slate-700 border-slate-200 hover:border-[#E11D48] hover:text-[#E11D48]"
                  }`}
                >
                  <Heart className={`w-3.5 h-3.5 ${ownedStatuses.has("favorite") ? "text-white fill-current" : "text-[#E11D48]"}`} /> {t("card_detail.favorite")}
                </button>
              </div>

              {/* Quantity stepper — visible once at least 1 owned, or when user wants to add more */}
              <div className="mt-3 flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                  {t("card_detail.quantity_label", "Exemplaires possédés")}
                  {ownedQuantity > 1 && (
                    <span className="ml-2 text-emerald-700 font-mono">× {ownedQuantity}</span>
                  )}
                </div>
                <QuantityStepper
                  cardId={id}
                  status="owned"
                  initialQuantity={ownedQuantity}
                  size="sm"
                  onChange={(q) => {
                    setOwnedQuantity(q);
                    setOwnedStatuses((cur) => {
                      const next = new Set(cur);
                      if (q > 0) next.add("owned");
                      else next.delete("owned");
                      return next;
                    });
                  }}
                />
              </div>
            </>
          )}
        </div>

        {/* DETAILS */}
        <div>
          <div className="border-b border-slate-200 pb-6 mb-6">
            <div className="text-xs font-semibold text-[#E11D48] uppercase tracking-wider mb-2">
              {card.set?.series} · {card.set?.name}
            </div>
            <h1 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tight leading-none text-slate-900">{card.name}</h1>
            <div className="flex flex-wrap gap-2 mt-4 text-xs">
              <span className="pill">#{card.number}/{card.set?.total}</span>
              {card.rarity && <span className="pill pill-yellow">{card.rarity}</span>}
              {card.supertype && <span className="pill pill-blue">{card.supertype}</span>}
              {card.hp && <span className="pill">HP {card.hp}</span>}
            </div>
          </div>

          {/* Per-market averages — EU (Cardmarket / EUR) + US (TCGPlayer / USD) + JP (coming) */}
          <div className="mb-6">
            <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#E11D48]" /> {t("card_detail.market_averages")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="markets-grid">
              {MARKETS.map((m) => {
                const agg = priceData.aggregates?.find((a) => a.market === m.id);
                const isPending = m.pending && !agg;
                // "Awaiting" = no aggregate AND non-JP market (typically newly released set
                // that pokemontcg.io / eBay haven't indexed yet). Friendlier than "no_data".
                const isAwaiting = !agg && !isPending && m.id !== "jp";
                return (
                  <div
                    key={m.id}
                    data-testid={`market-card-${m.id}`}
                    className={`flat-card p-4 text-left relative ${(isPending || isAwaiting) ? "opacity-60" : ""}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="lang-chip">{m.flag} {t(m.labelKey)}</span>
                      <span className="text-[10px] text-slate-400">
                        {agg
                          ? `${agg.count} ${t("card_detail.sales")}`
                          : isPending
                            ? t("card_detail.coming_soon", "Bientôt")
                            : t("card_detail.awaiting_short", "À venir")}
                      </span>
                    </div>
                    <div className="font-bold text-2xl text-slate-900">
                      {agg ? fmtCurrency(agg.avg_price, agg.currency) : "—"}
                    </div>
                    {agg && (
                      <div className="text-[10px] text-slate-500 mt-1">
                        {t("card_detail.min")} {fmtCurrency(agg.min_price, agg.currency)} · {t("card_detail.max")} {fmtCurrency(agg.max_price, agg.currency)}
                      </div>
                    )}
                    {agg && agg.trend_pct !== null && agg.trend_pct !== undefined && (
                      <TrendPill pct={agg.trend_pct} t={t} />
                    )}
                    {isPending && (
                      <div className="text-[10px] text-slate-500 mt-1 italic">
                        {t("card_detail.ebay_pending", "En attente des données eBay")}
                      </div>
                    )}
                    {isAwaiting && (
                      <div className="text-[10px] text-slate-500 mt-1 italic">
                        {m.id === "us"
                          ? t("card_detail.us_awaiting", "Set trop récent — données TCGplayer pas encore indexées")
                          : t("card_detail.eu_awaiting", "Aucune vente Cardmarket récente")}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] text-slate-400 mt-2 font-mono">
              {t("card_detail.market_disclaimer", "EU = prix moyen Cardmarket (toutes langues confondues). US = prix moyen TCGPlayer.")}
            </div>
          </div>

          {/* Filter & submit */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {user && user.is_premium && (
              <PriceAlertButton cardId={id} />
            )}
            {user?.role === "admin" && (
              <button
                onClick={() => setShowForm((s) => !s)}
                data-testid="toggle-submit-price-btn"
                className="btn-ghost ml-auto text-xs uppercase tracking-wider inline-flex items-center gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" /> {t("card_detail.submit_price")}
              </button>
            )}
          </div>

          {priceData.history_limited && (
            <div className="rounded-xl border border-[#FACC15]/40 bg-amber-50 p-3 mb-4 flex items-center gap-3" data-testid="history-limit-banner">
              <Crown className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <div className="text-xs text-slate-700 flex-1">
                {t("card_detail.history_limited", { days: priceData.history_days })}{" "}
                <Link to="/pricing" className="text-[#E11D48] font-semibold hover:underline ml-1">{t("card_detail.upgrade_premium")}</Link> {t("card_detail.for_full_history")}
              </div>
            </div>
          )}

          {showForm && user && (
            <PriceForm cardId={id} onSubmitted={() => { loadPrices(); setShowForm(false); }} />
          )}

          {/* Long-term monthly chart since release */}
          <LongTermPriceChart cardId={id} cardSetReleaseDate={card.set?.releaseDate} />

          {/* Short-term entries chart */}
          {chartData.length > 1 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6 mt-5 shadow-sm">
              <div className="font-mono text-[10px] text-slate-400 uppercase tracking-widest mb-3">{t("card_detail.history_chart")}</div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                  <Line type="monotone" dataKey="price" stroke="#E11D48" strokeWidth={2} dot={{ fill: "#E11D48", r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Entries list */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden mt-5 shadow-sm">
            <div className="px-5 py-3 border-b border-slate-200 font-mono text-[10px] text-slate-400 uppercase tracking-widest flex justify-between">
              <span>{t("card_detail.sale_entries")} ({filteredEntries.length})</span>
              <span>{t("card_detail.latest_first")}</span>
            </div>
            {filteredEntries.length === 0 ? (
              <div className="p-10 text-center text-slate-400 text-sm">
                {t("card_detail.no_entries")}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {filteredEntries.map((e) => (
                  <li key={e.id} className="px-5 py-3 grid grid-cols-[80px,1fr,80px,auto] gap-4 items-center text-sm">
                    <span className="font-mono font-medium text-base text-slate-900">{fmtCurrency(e.price, e.currency)}</span>
                    <div className="text-slate-600 text-xs">
                      <div>{CONDITION_LABELS[e.condition]} · {SOURCE_LABELS[e.source]}</div>
                      {e.notes && <div className="text-slate-400 text-[11px] mt-0.5">{e.notes}</div>}
                    </div>
                    <span className="font-mono text-[10px] text-slate-400">{e.sale_date}</span>
                    {e.url ? (
                      <a href={e.url} target="_blank" rel="noreferrer" className="text-[#E11D48] hover:underline" data-testid={`entry-link-${e.id}`}>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    ) : <span />}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PriceForm({ cardId, onSubmitted }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    language: "en",
    price: "",
    currency: "EUR",
    condition: "near_mint",
    source: "ebay",
    sale_date: new Date().toISOString().slice(0, 10),
    url: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.price || parseFloat(form.price) <= 0) {
      toast.error(t("card_detail.enter_valid_price"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/prices", {
        ...form,
        card_id: cardId,
        price: parseFloat(form.price),
      });
      toast.success(t("card_detail.price_submitted"));
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="flat-card p-5 mt-2 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="price-form">
      <label className="flex flex-col gap-1 text-xs font-mono tracking-wider uppercase text-white/50">
        {t("card_detail.form.language")}
        <select value={form.language} onChange={(e) => update("language", e.target.value)} data-testid="form-language">
          {Object.keys(LANGUAGE_FLAGS).slice(0, 6).map((l) => <option key={l} value={l}>{LANGUAGE_LABELS[l]}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs font-mono tracking-wider uppercase text-white/50">
        {t("card_detail.form.price")}
        <input type="number" step="0.01" min="0" value={form.price} onChange={(e) => update("price", e.target.value)} data-testid="form-price" placeholder="0.00" required />
      </label>
      <label className="flex flex-col gap-1 text-xs font-mono tracking-wider uppercase text-white/50">
        {t("card_detail.form.currency")}
        <select value={form.currency} onChange={(e) => update("currency", e.target.value)} data-testid="form-currency">
          <option>EUR</option><option>USD</option><option>JPY</option><option>GBP</option>
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs font-mono tracking-wider uppercase text-white/50">
        {t("card_detail.form.date")}
        <input type="date" value={form.sale_date} onChange={(e) => update("sale_date", e.target.value)} data-testid="form-date" />
      </label>
      <label className="flex flex-col gap-1 text-xs font-mono tracking-wider uppercase text-white/50">
        {t("card_detail.form.condition")}
        <select value={form.condition} onChange={(e) => update("condition", e.target.value)} data-testid="form-condition">
          {Object.keys(CONDITION_LABELS).map((c) => <option key={c} value={c}>{CONDITION_LABELS[c]}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs font-mono tracking-wider uppercase text-white/50">
        {t("card_detail.form.source")}
        <select value={form.source} onChange={(e) => update("source", e.target.value)} data-testid="form-source">
          {Object.keys(SOURCE_LABELS).map((s) => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs font-mono tracking-wider uppercase text-white/50 md:col-span-2">
        {t("card_detail.form.url")} <span className="normal-case text-white/30">{t("card_detail.form.optional")}</span>
        <input value={form.url} onChange={(e) => update("url", e.target.value)} placeholder="https://ebay.com/itm/…" data-testid="form-url" />
      </label>
      <label className="flex flex-col gap-1 text-xs font-mono tracking-wider uppercase text-white/50 md:col-span-4">
        {t("card_detail.form.notes")} <span className="normal-case text-white/30">{t("card_detail.form.optional")}</span>
        <input value={form.notes} onChange={(e) => update("notes", e.target.value)} placeholder={t("card_detail.form.notes_placeholder")} data-testid="form-notes" />
      </label>
      <button type="submit" disabled={submitting} data-testid="form-submit-btn" className="btn-primary md:col-span-4 mt-2 text-xs uppercase tracking-wider">
        {submitting ? t("card_detail.form.submitting") : t("card_detail.form.submit")}
      </button>
    </form>
  );
}


function PriceAlertButton({ cardId }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [threshold, setThreshold] = useState("");
  const [direction, setDirection] = useState("below");
  const [language, setLanguage] = useState("en");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!threshold || parseFloat(threshold) <= 0) {
      toast.error(t("card_detail.enter_valid_threshold"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/alerts", {
        card_id: cardId,
        language,
        direction,
        threshold: parseFloat(threshold),
      });
      toast.success(t("card_detail.alert.created", { direction: t(`card_detail.alert.${direction}`), threshold }));
      setOpen(false);
      setThreshold("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="open-alert-btn"
        className="btn-ghost text-xs uppercase tracking-wider inline-flex items-center gap-1.5 border-[#FACC15]/40 text-[#FACC15]"
      >
        <Bell className="w-3.5 h-3.5" /> {t("card_detail.price_alert")}
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="flat-card p-2 inline-flex items-center gap-2 ml-1" data-testid="alert-form">
      <Bell className="w-3.5 h-3.5 text-[#FACC15]" />
      <select value={language} onChange={(e) => setLanguage(e.target.value)} className="text-xs py-1 px-2" data-testid="alert-language">
        {Object.keys(LANGUAGE_FLAGS).slice(0, 6).map((l) => <option key={l} value={l}>{LANGUAGE_FLAGS[l]}</option>)}
      </select>
      <select value={direction} onChange={(e) => setDirection(e.target.value)} className="text-xs py-1 px-2" data-testid="alert-direction">
        <option value="below">{t("card_detail.alert.below")}</option>
        <option value="above">{t("card_detail.alert.above")}</option>
      </select>
      <input type="number" step="0.01" min="0" placeholder={t("card_detail.alert.placeholder")} value={threshold} onChange={(e) => setThreshold(e.target.value)} className="text-xs py-1 px-2 w-20" data-testid="alert-threshold" />
      <button type="submit" disabled={submitting} data-testid="alert-submit" className="btn-primary text-xs uppercase tracking-wider py-1.5 px-3">{t("card_detail.alert.save")}</button>
      <button type="button" onClick={() => setOpen(false)} className="text-white/40 hover:text-white text-xs px-2" data-testid="alert-cancel">×</button>
    </form>
  );
}

function TrendPill({ pct, t }) {
  const isUp = pct > 0.5;
  const isDown = pct < -0.5;
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  const color = isUp ? "text-emerald-600 bg-emerald-50 border-emerald-200"
    : isDown ? "text-[#E11D48] bg-rose-50 border-rose-200"
    : "text-slate-500 bg-slate-50 border-slate-200";
  const sign = pct > 0 ? "+" : "";
  return (
    <div
      data-testid="trend-pill"
      className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono font-semibold ${color}`}
      title={t("card_detail.trend_tooltip", "Variation moyenne sur les 14 derniers jours vs les 30 jours précédents")}
    >
      <Icon className="w-3 h-3" />
      {sign}{pct}%
      <span className="font-normal text-[9px] opacity-70">· {t("card_detail.trend_window")}</span>
    </div>
  );
}

function LongTermPriceChart({ cardId, cardSetReleaseDate }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/prices/${cardId}/history`)
      .then((r) => setData(r.data))
      .catch(() => setData({ buckets: [] }))
      .finally(() => setLoading(false));
  }, [cardId]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mt-5 text-center font-mono text-xs text-slate-400 py-12 tracking-widest">
        {t("card_detail.loading_chart")}
      </div>
    );
  }
  if (!data) return null;

  const hasAnyData = data.buckets && data.buckets.some((b) => b.avg_price !== null);
  const release = data.release_date || cardSetReleaseDate;
  const releaseLabel = release ? release.replace(/\//g, "-").slice(0, 7) : null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 mt-5 shadow-sm" data-testid="long-term-price-chart">
      <ChartHeader data={data} release={release} />
      {!hasAnyData ? (
        <div className="text-center text-slate-400 text-sm py-10">
          {t("card_detail.no_long_term_data", "Pas encore d'historique pour cette carte.")}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data.buckets.map((b) => ({ month: b.month, price: b.avg_price }))} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="gPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#E11D48" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#E11D48" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#e2e8f0" />
            <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 10, fontFamily: "JetBrains Mono" }} interval="preserveStartEnd" />
            <YAxis tick={{ fill: "#94a3b8", fontSize: 10, fontFamily: "JetBrains Mono" }} />
            <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", fontFamily: "JetBrains Mono", fontSize: 12 }} />
            {releaseLabel && (
              <ReferenceLine
                x={releaseLabel}
                stroke="#E11D48"
                strokeDasharray="3 3"
                label={{ value: t("card_detail.release_marker", "SORTIE"), position: "insideTopLeft", fill: "#E11D48", fontSize: 9, fontFamily: "JetBrains Mono" }}
              />
            )}
            <Area type="monotone" dataKey="price" stroke="#E11D48" strokeWidth={2} fill="url(#gPrice)" connectNulls />
          </AreaChart>
        </ResponsiveContainer>
      )}
      {data.history_capped && <UpsellNote release={release} />}
    </div>
  );
}

function ChartHeader({ data, release }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <div>
        <div className="font-mono text-[10px] text-slate-400 uppercase tracking-widest">
          {data?.is_premium
            ? t("card_detail.chart_label_premium", "Évolution depuis la sortie")
            : t("card_detail.chart_label_free", "Évolution sur les 6 derniers mois")}
        </div>
        {release && (
          <div className="font-mono text-[10px] text-slate-400 tracking-wider mt-0.5">
            {t("card_detail.released")} {release}
          </div>
        )}
      </div>
    </div>
  );
}

function UpsellNote({ release }) {
  const { t } = useTranslation();
  return (
    <div className="text-center text-xs text-slate-500 mt-2 font-mono tracking-wider">
      <Crown className="w-3 h-3 inline text-[#FACC15] mr-1" />
      {t("card_detail.upgrade_premium")}
      {release ? ` — ${release}` : ""}
    </div>
  );
}

